/*
 * Teknik ekran: kurulumun o anki yükü ve çalışma biçimi.
 *
 * "Site yavaş" şikâyetinin yanıtı bugüne kadar yalnızca sunucudaki kayıtlarda
 * duruyordu — yöneticinin eline geçmeyen bir yerde. Bu sayfa aynı bilgiyi
 * panele taşır: kaç çekirdek çalışıyor, her biri ne kadar yüklü, hangi uç kaç
 * istek alıyor, hangisi yavaş, hangisi hata veriyor.
 *
 * Panelde "çekirdek" diye çizilen şey bir uvicorn worker sürecidir (bkz.
 * deploy/start.sh); işletim sistemi onu makinenin işlemci çekirdeklerine
 * dağıtır. Makinenin kendi çekirdekleri ayrıca "Sunucu işlemcisi" satırında
 * gösterilir — biri "uygulama ne kadar çalışıyor", diğeri "makinede ne kadar
 * yer kaldı" sorusudur.
 *
 * İki yoklama hızı vardır. Olağan modda sayfa 30 saniyede bir tazelenir;
 * **Canlı Takip** açıkken çekirdek şeridi iki saniyede bir hafif bir uçtan
 * (`/admin/technical/live`) okunur ve grafikler akar. Uç kırılımı dakikalık
 * kovalardan geldiği için canlı modda sık sorulmaz. Sekme arka plandayken
 * canlı yoklama durur: ölçtüğü yükü kendisi üretmemelidir.
 *
 * Sayfa hiçbir şeyi değiştirmez, yalnızca okur; yetkisi `admin.manage`'dir.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity, AlertTriangle, Cpu, Crown, Database, Gauge, Layers, Loader2,
  Radio, RefreshCw, Timer,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';

const POLL_MS = 30000;
const LIVE_POLL_MS = 2000;
// Akan grafiğin tuttuğu nokta sayısı: canlı modda iki dakika.
export const HISTORY_POINTS = 60;

const RANGES = [
  { value: 15, label: 'Son 15 dakika' },
  { value: 60, label: 'Son 1 saat' },
  { value: 360, label: 'Son 6 saat' },
  { value: 720, label: 'Son 12 saat' },
];

const nf = new Intl.NumberFormat('tr-TR');
const nf1 = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 });

const fmt = (value) => (
  value === null || value === undefined ? '—' : nf.format(value));

const fmt1 = (value) => (
  value === null || value === undefined ? '—' : nf1.format(value));

const fmtMs = (value) => (
  value === null || value === undefined ? '—' : `${nf.format(Math.round(value))} ms`);

const fmtUptime = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days) return `${days} gün ${hours} sa`;
  if (hours) return `${hours} sa ${minutes} dk`;
  if (minutes) return `${minutes} dk`;
  return `${total} sn`;
};

const fmtCpuTime = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${nf1.format(seconds)} sn`;
  if (seconds < 3600) return `${nf1.format(seconds / 60)} dk`;
  return `${nf1.format(seconds / 3600)} sa`;
};

// Tek bir süreç en çok bir çekirdeği doldurur; %85 üstü doygunluktur.
export const cpuTone = (value) => {
  if (value === null || value === undefined) return 'muted';
  if (value >= 85) return 'danger';
  if (value >= 60) return 'warn';
  return 'ok';
};

// Olay döngüsü gecikmesi isteklerin kuyrukta ne kadar beklediğidir.
export const lagTone = (value) => {
  if (value === null || value === undefined) return 'muted';
  if (value >= 200) return 'danger';
  if (value >= 50) return 'warn';
  return 'ok';
};

// Havuzun tavanına dayanmak doygunluktur; dörtte üçü uyarıdır.
export const poolTone = (used, max) => {
  if (used === null || used === undefined || !max) return undefined;
  if (used >= max) return 'danger';
  if (used >= max * 0.75) return 'warn';
  return undefined;
};

const fmtPool = (used, max) => (
  used === null || used === undefined || !max ? '—' : `${fmt(used)} / ${fmt(max)}`);

// Çekirdek sayaçları anlıktır ve açılıştan beri toplanır; alttaki tablolar
// dakikalık kovalardan okunur ve içinde bulunulan dakika yazılmadan görünmez.
// İki sayının aynı anda farklı söylemesi bu yüzdendir.
export const TOTAL_ERRORS_HINT = 'Çekirdeğin açılışından beri sunucu hatası (5xx). '
  + 'Hangi adres olduğu alttaki "Hata veren uçlar" tablosunda, sayaç yazılınca '
  + '(1–2 dakika içinde) görünür.';

const TONE_TEXT = {
  ok: 'text-emerald-400',
  warn: 'text-amber-400',
  danger: 'text-red-400',
  muted: 'text-slate-400',
};

const TONE_STROKE = {
  ok: '#34d399',
  warn: '#fbbf24',
  danger: '#f87171',
  muted: '#94a3b8',
};

function Card({ icon: Icon, title, value, hint, tone }) {
  return (
    <div className="admin-card p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400">
        {Icon ? <Icon className="w-4 h-4" /> : null}
        <span>{title}</span>
      </div>
      <div className={`text-2xl font-semibold ${TONE_TEXT[tone] || TONE_TEXT.ok}`}>{value}</div>
      {hint ? <div className="text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

/* Yükseklik sabit, genişlik kapsayıcıya uyar; eksen yoktur — amaç eğilimi
 * göstermektir, değer zaten kartın üstünde yazar. */
function Sparkline({ points, max = 100, tone = 'ok', height = 44 }) {
  const width = 240;
  if (!points || points.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-[11px] text-slate-500 rounded bg-slate-900/40"
        style={{ height }}
      >
        Canlı takip açıldığında grafik akar
      </div>
    );
  }
  const top = Math.max(max, ...points, 1);
  const step = width / (HISTORY_POINTS - 1);
  const offset = (HISTORY_POINTS - points.length) * step;
  const coords = points.map((value, index) => {
    const x = offset + index * step;
    const y = height - (Math.max(0, value) / top) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const area = `${offset.toFixed(1)},${height} ${coords.join(' ')} ${width},${height}`;
  const stroke = TONE_STROKE[tone] || TONE_STROKE.ok;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full rounded bg-slate-900/40"
      style={{ height }}
      aria-hidden="true"
    >
      <polygon points={area} fill={stroke} opacity="0.12" />
      <polyline points={coords.join(' ')} fill="none" stroke={stroke} strokeWidth="1.6" />
    </svg>
  );
}

function Bar({ value, tone }) {
  const width = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="h-1.5 w-full rounded bg-slate-800 overflow-hidden">
      <div
        className="h-full rounded transition-all duration-500"
        style={{ width: `${width}%`, background: TONE_STROKE[tone] || TONE_STROKE.ok }}
      />
    </div>
  );
}

function Stat({ label, value, tone, title }) {
  return (
    <div className="flex flex-col" title={title}>
      <span className="text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`text-sm font-medium tabular-nums ${tone ? TONE_TEXT[tone] : 'text-slate-200'}`}>
        {value}
      </span>
    </div>
  );
}

function CoreCard({ index, worker, history }) {
  const tone = cpuTone(worker.cpu_percent);
  return (
    <div className="admin-card p-4 flex flex-col gap-3" data-testid="core-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-100">{index}. Çekirdek</span>
        </div>
        {worker.is_leader ? (
          <span
            className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300"
            title="Gecelik yedek, lisans kontrolü ve yatırım zaman aşımı bu çekirdekte çalışıyor"
          >
            <Crown className="w-3 h-3" /> Arka plan işleri
          </span>
        ) : null}
      </div>

      <div className="flex items-end justify-between gap-2">
        <div>
          <div className={`text-3xl font-semibold tabular-nums ${TONE_TEXT[tone]}`}>
            %{fmt1(worker.cpu_percent)}
          </div>
          <div className="text-xs text-slate-400">anlık işlemci kullanımı</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold tabular-nums text-slate-100">
            {fmt1(worker.req_per_sec)}
          </div>
          <div className="text-xs text-slate-400">istek / sn</div>
        </div>
      </div>

      <Bar value={worker.cpu_percent} tone={tone} />
      <Sparkline points={history?.cpu} tone={tone} />

      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        <Stat label="Gecikme" value={fmtMs(worker.loop_lag_ms)} tone={lagTone(worker.loop_lag_ms)} />
        <Stat label="İşlenen" value={fmt(worker.inflight)} />
        <Stat label="Bellek" value={worker.rss_mb ? `${fmt(Math.round(worker.rss_mb))} MB` : '—'} />
        <Stat label="Toplam istek" value={fmt(worker.requests_total)} />
        <Stat
          label="Toplam hata"
          value={fmt(worker.errors_total)}
          tone={worker.errors_total ? 'danger' : undefined}
          title={TOTAL_ERRORS_HINT}
        />
        <Stat label="CPU süresi" value={fmtCpuTime(worker.cpu_seconds)} />
        <Stat label="Ayakta" value={fmtUptime(worker.uptime_seconds)} />
        <Stat
          label="Veritabanı"
          value={fmtPool(worker.pool_used, worker.pool_max)}
          tone={poolTone(worker.pool_used, worker.pool_max)}
        />
        <Stat label="Süreç" value={worker.pid ?? '—'} />
      </div>
    </div>
  );
}

function TotalCard({ workers, history, configured, alive }) {
  const sum = (key) => workers.reduce((acc, w) => acc + (Number(w[key]) || 0), 0);
  const count = workers.length || 1;
  const average = sum('cpu_percent') / count;
  const tone = cpuTone(average);
  const missing = alive != null && configured && alive < configured;
  const maxLag = workers.reduce((acc, w) => Math.max(acc, Number(w.loop_lag_ms) || 0), 0);
  const pool = poolTotals(workers);
  return (
    <div className="admin-card p-4 flex flex-col gap-3 border border-slate-700/60">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-100">Toplam</span>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${missing
          ? 'bg-red-500/15 text-red-300'
          : 'bg-emerald-500/15 text-emerald-300'}`}
        >
          {missing ? `${alive} / ${configured} çekirdek` : `${workers.length} çekirdek`}
        </span>
      </div>

      <div className="flex items-end justify-between gap-2">
        <div>
          <div className={`text-3xl font-semibold tabular-nums ${TONE_TEXT[tone]}`}>
            %{fmt1(average)}
          </div>
          <div className="text-xs text-slate-400">çekirdek başına ortalama</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold tabular-nums text-slate-100">
            {fmt1(sum('req_per_sec'))}
          </div>
          <div className="text-xs text-slate-400">istek / sn</div>
        </div>
      </div>

      <Bar value={average} tone={tone} />
      <Sparkline points={history} tone={tone} />

      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        <Stat label="En yüksek gecikme" value={fmtMs(maxLag)} tone={lagTone(maxLag)} />
        <Stat label="İşlenen" value={fmt(sum('inflight'))} />
        <Stat label="Bellek" value={`${fmt(Math.round(sum('rss_mb')))} MB`} />
        <Stat label="Toplam istek" value={fmt(sum('requests_total'))} />
        <Stat
          label="Toplam hata"
          value={fmt(sum('errors_total'))}
          tone={sum('errors_total') ? 'danger' : undefined}
          title={TOTAL_ERRORS_HINT}
        />
        <Stat label="CPU süresi" value={fmtCpuTime(sum('cpu_seconds'))} />
        <Stat
          label="Veritabanı"
          value={fmtPool(pool.used, pool.max)}
          tone={poolTone(pool.used, pool.max)}
        />
      </div>
      {missing ? (
        <p className="text-xs text-red-300">
          Bir çekirdek düşmüş; yenisi kendiliğinden açılır. Sürekli eksik
          görünüyorsa sunucu ekibine bildirin.
        </p>
      ) : null}
    </div>
  );
}

function HostCores({ cores, load }) {
  if (!cores || !cores.length) return null;
  return (
    <div className="admin-card p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Gauge className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-100">Sunucu işlemcisi</span>
          <span className="text-xs text-slate-500">
            makinenin kendi çekirdekleri — MySQL ve komşu kurulumlar dahil
          </span>
        </div>
        {load ? (
          <span className="text-xs text-slate-400 tabular-nums">
            Yük ortalaması: {load.map((v) => nf.format(v)).join(' · ')}
            <span className="text-slate-500"> (1 · 5 · 15 dk)</span>
          </span>
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cores.map((value, index) => {
          const tone = cpuTone(value);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">İşlemci {index + 1}</span>
                <span className={`tabular-nums ${TONE_TEXT[tone]}`}>%{fmt1(value)}</span>
              </div>
              <Bar value={value} tone={tone} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RouteTable({ title, rows, columns, empty }) {
  return (
    <div className="admin-card p-4 flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-slate-200">{title}</h3>
      {rows && rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3">Uç</th>
                {columns.map((column) => (
                  <th key={column.key} className="py-2 pr-3 text-right">{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.method}-${row.route}`} className="border-t border-slate-700/60">
                  <td className="py-2 pr-3">
                    <span className="text-slate-400 mr-2">{row.method}</span>
                    <span className="text-slate-200 break-all">{row.route}</span>
                  </td>
                  {columns.map((column) => (
                    <td key={column.key} className="py-2 pr-3 text-right tabular-nums text-slate-300">
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-400">{empty}</p>
      )}
    </div>
  );
}

const workerKey = (worker) => `${worker.host || ''}:${worker.pid}`;

/* Havuz çekirdek başınadır; toplam, bütün çekirdeklerin veritabanına aynı
 * anda açabileceği bağlantıdır. Havuzunu bildirmeyen çekirdek toplama girmez
 * — eksik bir sayıyı sıfır saymak doluluğu olduğundan düşük gösterirdi. */
export function poolTotals(workers) {
  const known = workers.filter((w) => w.pool_max);
  if (!known.length) return { used: null, max: null };
  return {
    used: known.reduce((acc, w) => acc + (Number(w.pool_used) || 0), 0),
    max: known.reduce((acc, w) => acc + (Number(w.pool_max) || 0), 0),
  };
}

/* Grafik geçmişi istemcidedir: sunucu yalnızca anlık değeri bilir. Anahtar
 * süreçtir; ölen çekirdeğin geçmişi atılır, yeni açılanınki boş başlar. */
export function appendHistory(previous, workers) {
  const next = {};
  workers.forEach((worker) => {
    const key = workerKey(worker);
    const old = previous[key] || { cpu: [], rps: [] };
    next[key] = {
      cpu: [...old.cpu, Number(worker.cpu_percent) || 0].slice(-HISTORY_POINTS),
      rps: [...old.rps, Number(worker.req_per_sec) || 0].slice(-HISTORY_POINTS),
    };
  });
  let total = previous.__total || [];
  if (workers.length) {
    const average = workers.reduce((acc, w) => acc + (Number(w.cpu_percent) || 0), 0)
      / workers.length;
    total = [...total, average].slice(-HISTORY_POINTS);
  }
  next.__total = total;
  return next;
}

export default function Technical() {
  const { toast } = useToast();
  const [minutes, setMinutes] = useState(60);
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [liveMode, setLiveMode] = useState(false);
  const [history, setHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const liveBusy = useRef(false);

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const response = await client.get('/admin/technical', { params: { minutes } });
      setData(response.data);
      setError('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Teknik veriler alınamadı';
      setError(detail);
      if (!quiet) toast({ title: detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [minutes, toast]);

  const loadLive = useCallback(async () => {
    // Yanıt gelmeden ikinci istek atılmaz: yavaş bir anda istekler üst üste
    // binip ölçtüğümüz yükü büyütürdü.
    if (liveBusy.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    liveBusy.current = true;
    try {
      const response = await client.get('/admin/technical/live');
      setLive(response.data);
      setHistory((prev) => appendHistory(prev, response.data?.workers || []));
    } catch (err) {
      // Canlı akışta tek bir kayıp yoklama hata değildir; bir sonraki tur dener.
    } finally {
      liveBusy.current = false;
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!liveMode) return undefined;
    loadLive();
    const timer = setInterval(loadLive, LIVE_POLL_MS);
    return () => clearInterval(timer);
  }, [liveMode, loadLive]);

  const requests = data?.requests;
  const runtime = data?.runtime;
  const total = requests?.total;
  const perMinute = requests?.per_minute || [];
  const perMinuteAvg = total && requests?.minutes
    ? Math.round(total.hits / requests.minutes) : null;
  const busiestMinute = perMinute.length
    ? Math.max(...perMinute.map((row) => row.hits)) : null;
  // Canlı modda şerit canlı uçtan, değilse sayfanın kendi yanıtından beslenir.
  const workers = (liveMode && live?.workers) || data?.workers || [];
  // Veritabanı kartı bütün çekirdeklerin toplamını yazar; çekirdek satırları
  // henüz yoksa (yeni açılış) yanıtı veren çekirdeğin havuzuna düşülür.
  const totals = poolTotals(workers);
  const ownPoolMax = runtime?.pool?.size === null || runtime?.pool?.size === undefined
    ? null
    : runtime.pool.size + (runtime.pool.max_overflow || 0);
  const poolUsed = totals.max ? totals.used : (runtime?.pool?.checked_out ?? null);
  const poolMax = totals.max || ownPoolMax;
  const cores = (liveMode && live?.cores) || data?.cores;
  const loadAvg = (liveMode && live?.load) || runtime?.process?.load;
  const configured = (liveMode && live?.configured_workers) || runtime?.workers;
  const alive = liveMode ? live?.workers_alive : runtime?.workers_alive;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Sistem & İstekler</h1>
          <p className="text-sm text-slate-400">
            Kurulumun o anki yükü ve çalışma biçimi. Sayfa hiçbir şeyi değiştirmez.
            {liveMode
              ? ' Canlı takip açık: çekirdekler iki saniyede bir tazelenir.'
              : ' 30 saniyede bir kendiliğinden tazelenir.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`admin-btn ${liveMode ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setLiveMode((value) => !value)}
            aria-pressed={liveMode}
          >
            {liveMode ? (
              <span className="relative flex w-2.5 h-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-emerald-400" />
              </span>
            ) : <Radio className="w-4 h-4" />}
            <span className="ml-2">{liveMode ? 'Canlı Takip Açık' : 'Canlı Takip'}</span>
          </button>
          {/* Süre seçimi uç tablolarının penceresidir; canlı takipte ekran
              anı gösterir ve seçici yalnızca kafa karıştırırdı. */}
          {liveMode ? null : (
            <select
              className="admin-input admin-input-w-lg"
              value={minutes}
              onChange={(event) => setMinutes(Number(event.target.value))}
            >
              {RANGES.map((range) => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          )}
          {/* Canlı takipte ekran kendiliğinden akar; elle yenileme gereksizdir. */}
          {liveMode ? null : (
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => load(false)}
              disabled={loading}
            >
              {loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2">Yenile</span>
            </button>
          )}
        </div>
      </div>

      {error ? (
        <div className="admin-card p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <p className="text-sm text-slate-200">{error}</p>
            <p className="text-xs text-slate-400">
              Bu ekran yalnızca okuma yapar; hata sitenin çalışmasını etkilemez.
            </p>
          </div>
        </div>
      ) : null}

      {/* Çekirdekler — her kart bir uvicorn süreci */}
      {workers.length ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {workers.map((worker, index) => (
            <CoreCard
              key={workerKey(worker)}
              index={index + 1}
              worker={worker}
              history={history[workerKey(worker)]}
            />
          ))}
          <TotalCard
            workers={workers}
            history={history.__total}
            configured={configured}
            alive={alive}
          />
        </div>
      ) : (
        <div className="admin-card p-4 text-sm text-slate-400">
          Çekirdek durumu henüz yazılmadı. Uygulama yeni açıldıysa birkaç
          saniye içinde görünür.
        </div>
      )}

      <HostCores cores={cores} load={loadAvg} />

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Card
          icon={Activity}
          title="Arka plan işleri"
          value={runtime?.background_running === null || runtime?.background_running === undefined
            ? '—'
            : runtime.background_running ? 'Çalışıyor' : 'Durdu'}
          hint="Gecelik yedek, lisans kontrolü, yatırım zaman aşımı"
          tone={runtime?.background_running === false ? 'danger' : 'ok'}
        />
        <Card
          icon={Database}
          title="Veritabanı bağlantısı"
          value={poolUsed === null ? '—' : `${fmt(poolUsed)} / ${fmt(poolMax)}`}
          hint={totals.max
            ? 'Bütün çekirdeklerde o an açık / en çok · gecikmeyle birlikte okuyun'
            : 'Yanıtı veren çekirdeğin havuzu'}
          tone={poolTone(poolUsed, poolMax) || 'ok'}
        />
        <Card
          icon={Gauge}
          title="Dakikada istek"
          value={fmt(perMinuteAvg)}
          hint={busiestMinute ? `En yoğun dakika: ${fmt(busiestMinute)}` : 'Ortalama'}
        />
        <Card
          icon={AlertTriangle}
          title="Sunucu hatası (5xx)"
          value={fmt(total?.errors)}
          hint={requests ? `Son ${requests.minutes} dakika · sıfır olmalı` : 'Sıfır olmalı'}
          tone={total?.errors ? 'danger' : 'ok'}
        />
        <Card
          icon={Activity}
          title="Toplam istek"
          value={fmt(total?.hits)}
          hint={requests ? `Son ${requests.minutes} dakika` : ''}
        />
        <Card
          icon={Timer}
          title="Ortalama yanıt"
          value={fmtMs(total?.avg_ms)}
          hint={total?.max_ms ? `En yavaş: ${fmtMs(total.max_ms)}` : ''}
          tone={total?.avg_ms > 500 ? 'warn' : 'ok'}
        />
        <Card
          icon={Cpu}
          title="Çekirdek sayısı"
          value={configured ? fmt(configured) : '—'}
          hint={configured > 1
            ? 'Sunucunun çekirdeklerine göre kendiliğinden seçildi'
            : 'Tek çekirdek — istekler sırayla işleniyor'}
          tone={configured > 1 ? 'ok' : 'warn'}
        />
        <Card
          icon={Timer}
          title="Uygulama ayakta"
          value={fmtUptime(runtime?.uptime_seconds)}
          hint="Yanıtı veren çekirdeğin son açılışından beri"
        />
      </div>

      <div className="grid gap-3 xl:grid-cols-2">
        <RouteTable
          title="En çok istek alan uçlar"
          rows={requests?.busiest}
          empty="Bu pencerede henüz kayıt yok. Sayaç dakikada bir yazılır."
          columns={[
            { key: 'hits', label: 'İstek', render: (row) => fmt(row.hits) },
            { key: 'avg', label: 'Ortalama', render: (row) => fmtMs(row.avg_ms) },
          ]}
        />
        <RouteTable
          title="En yavaş uçlar"
          rows={requests?.slowest}
          empty="Yeterli örnek yok (en az 5 istek gereken uçlar listelenir)."
          columns={[
            { key: 'avg', label: 'Ortalama', render: (row) => fmtMs(row.avg_ms) },
            { key: 'max', label: 'En yavaş', render: (row) => fmtMs(row.max_ms) },
            { key: 'hits', label: 'İstek', render: (row) => fmt(row.hits) },
          ]}
        />
      </div>

      <RouteTable
        title="Hata veren uçlar"
        rows={requests?.failing}
        empty="Bu pencerede sunucu hatası yok. Son 1–2 dakikanın hataları sayaç yazılınca burada görünür; çekirdek kartlarındaki sayı anlıktır."
        columns={[
          { key: 'errors', label: '5xx', render: (row) => fmt(row.errors) },
          { key: 'client', label: '4xx', render: (row) => fmt(row.client_errors) },
          { key: 'hits', label: 'İstek', render: (row) => fmt(row.hits) },
        ]}
      />

      <div className="admin-card p-4 text-xs text-slate-400 flex flex-wrap gap-x-6 gap-y-1">
        <span>Yanıtı veren süreç: {runtime?.process?.pid ?? '—'}</span>
        <span title="Henüz veritabanına yazılmamış dakikalık kayıtlar; dakikada bir yazılır">
          Bekleyen ölçüm: {fmt(requests?.pending_buckets)}
        </span>
        {requests?.dropped ? <span>Atlanan: {fmt(requests.dropped)}</span> : null}
        <span>
          Gecikme: çekirdeğin istekleri ne kadar beklettiği — 50 ms altı rahat,
          200 ms üstü doygun.
        </span>
      </div>
    </div>
  );
}
